import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface PricingDefaults {
  id: string;
  currency: string;
  subscriptionFeeUsd: number;
  subscriptionDiscountPct: number;
  includedDiscountPct: number;
  usageDiscountPct: number;
  profitMarginPct: number;
  version: string;
  effectiveFrom?: string;
  status: string;
  includedThresholdUsd: number;
}

export interface OrgPricingListItem {
  orgId: string;
  orgName: string;
  orgStatus: string;
  subscriptionFeeUsd: number;
  includedThresholdUsd: number;
  hasOverride: boolean;
}

export interface OrgPricingDetail {
  orgId: string;
  orgName: string;
  orgStatus: string;
  resolved: {
    currency: string;
    subscriptionFeeUsd: number;
    subscriptionDiscountPct: number;
    includedDiscountPct: number;
    usageDiscountPct: number;
    profitMarginPct: number;
    hasOrgOverride: boolean;
    source: string;
    includedThresholdUsd: number;
  };
  override: Record<string, unknown> | null;
}

export interface StaffAnalyticsSummary {
  from: string;
  to: string;
  operatorView: boolean;
  tokensTotal: number;
  tokensIn: number;
  tokensOut: number;
  sessions: number;
  exchangeCount: number;
  activeOrgs: number;
  platformCostUsd: number;
  estEarningsUsd: number;
  marginUsd: number;
  organisations: Array<{
    orgId: string;
    orgName: string;
    tokensTotal: number;
    exchangeCount: number;
    sessions: number;
    platformCostUsd: number;
    estSubscriptionUsd: number;
    estUsageUsd: number;
    estEarningsUsd: number;
    marginUsd: number;
  }>;
}

export interface StaffTimeseriesResponse {
  from: string;
  to: string;
  grain: string;
  metric: string;
  points: Array<{ bucket: string; value: number }>;
}

export interface StaffBreakdownResponse {
  from: string;
  to: string;
  dimension: string;
  items: Array<{
    key: string;
    label?: string;
    tokensTotal: number;
    exchangeCount: number;
    platformCostUsd: number;
    estSubscriptionUsd?: number | null;
    estUsageUsd?: number | null;
    estEarningsUsd?: number | null;
  }>;
}

export interface OrgSearchItem {
  orgId: string;
  name: string;
  status: string;
  businessType?: string;
  memberCount: number;
}

export interface OrgImpact {
  orgId: string;
  name: string;
  status: string;
  memberCount: number;
  applicationsCount: number;
  agentsCount: number;
  apiKeyProfilesCount: number;
  hasPricingOverride: boolean;
  hasBudgetConfig: boolean;
  billingProfilePresent: boolean;
}

export const staffApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPricingDefaults: builder.query<PricingDefaults, void>({
      query: () => '/portal/staff/pricing/defaults',
      transformResponse: (response: unknown) =>
        normalizeKeys<PricingDefaults>(response),
      providesTags: ['StaffPricing'],
    }),
    updatePricingDefaults: builder.mutation<
      PricingDefaults,
      {
        subscriptionFeeUsd: number;
        subscriptionDiscountPct: number;
        includedDiscountPct: number;
        usageDiscountPct: number;
        profitMarginPct: number;
        note: string;
        currency?: string;
      }
    >({
      query: (body) => ({
        url: '/portal/staff/pricing/defaults',
        method: 'PUT',
        body: {
          subscription_fee_usd: body.subscriptionFeeUsd,
          subscription_discount_pct: body.subscriptionDiscountPct,
          included_discount_pct: body.includedDiscountPct,
          usage_discount_pct: body.usageDiscountPct,
          profit_margin_pct: body.profitMarginPct,
          note: body.note,
          currency: body.currency || 'USD',
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<PricingDefaults>(response),
      invalidatesTags: ['StaffPricing'],
    }),
    listOrgPricing: builder.query<
      { items: OrgPricingListItem[]; total: number },
      { q?: string } | void
    >({
      query: (args) => ({
        url: '/portal/staff/pricing/orgs',
        params: { q: args && 'q' in args ? args.q : undefined },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ items: OrgPricingListItem[]; total: number }>(response),
      providesTags: ['StaffPricing'],
    }),
    getOrgPricing: builder.query<OrgPricingDetail, string>({
      query: (orgId) => `/portal/staff/pricing/orgs/${orgId}`,
      transformResponse: (response: unknown) =>
        normalizeKeys<OrgPricingDetail>(response),
      providesTags: (_r, _e, orgId) => [{ type: 'StaffPricing', id: orgId }],
    }),
    updateOrgPricing: builder.mutation<
      OrgPricingDetail,
      {
        orgId: string;
        subscriptionFeeUsd?: number | null;
        subscriptionDiscountPct?: number | null;
        includedDiscountPct?: number | null;
        usageDiscountPct?: number | null;
        profitMarginPct?: number | null;
        note: string;
      }
    >({
      query: ({ orgId, note, ...fields }) => ({
        url: `/portal/staff/pricing/orgs/${orgId}`,
        method: 'PUT',
        body: {
          subscription_fee_usd: fields.subscriptionFeeUsd,
          subscription_discount_pct: fields.subscriptionDiscountPct,
          included_discount_pct: fields.includedDiscountPct,
          usage_discount_pct: fields.usageDiscountPct,
          profit_margin_pct: fields.profitMarginPct,
          note,
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<OrgPricingDetail>(response),
      invalidatesTags: ['StaffPricing'],
    }),
    resetOrgPricing: builder.mutation<
      OrgPricingDetail,
      { orgId: string; note?: string }
    >({
      query: ({ orgId, note }) => ({
        url: `/portal/staff/pricing/orgs/${orgId}/reset`,
        method: 'POST',
        body: { note },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<OrgPricingDetail>(response),
      invalidatesTags: ['StaffPricing'],
    }),
    getStaffAnalyticsSummary: builder.query<
      StaffAnalyticsSummary,
      { from?: string; to?: string; orgId?: string } | void
    >({
      query: (args) => ({
        url: '/portal/staff/analytics/summary',
        params: {
          from: args && 'from' in args ? args.from : undefined,
          to: args && 'to' in args ? args.to : undefined,
          org_id: args && 'orgId' in args ? args.orgId : undefined,
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<StaffAnalyticsSummary>(response),
      providesTags: ['StaffAnalytics'],
    }),
    getStaffAnalyticsTimeseries: builder.query<
      StaffTimeseriesResponse,
      { from?: string; to?: string; grain?: string; metric?: string }
    >({
      query: (args) => ({
        url: '/portal/staff/analytics/timeseries',
        params: {
          from: args.from,
          to: args.to,
          grain: args.grain,
          metric: args.metric,
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<StaffTimeseriesResponse>(response),
      providesTags: ['StaffAnalytics'],
    }),
    getStaffAnalyticsBreakdown: builder.query<
      StaffBreakdownResponse,
      { from?: string; to?: string; dimension?: string }
    >({
      query: (args) => ({
        url: '/portal/staff/analytics/breakdown',
        params: {
          from: args.from,
          to: args.to,
          dimension: args.dimension || 'org',
        },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<StaffBreakdownResponse>(response),
      providesTags: ['StaffAnalytics'],
    }),
    searchStaffOrganisations: builder.query<
      { items: OrgSearchItem[]; total: number },
      { q?: string } | void
    >({
      query: (args) => ({
        url: '/portal/staff/organisations',
        params: { q: args && 'q' in args ? args.q : undefined },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ items: OrgSearchItem[]; total: number }>(response),
    }),
    searchSuperOrganisations: builder.query<
      { items: OrgSearchItem[]; total: number },
      { q?: string } | void
    >({
      query: (args) => ({
        url: '/portal/super/organisations',
        params: { q: args && 'q' in args ? args.q : undefined },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{ items: OrgSearchItem[]; total: number }>(response),
    }),
    getOrganisationImpact: builder.query<OrgImpact, string>({
      query: (orgId) => `/portal/super/organisations/${orgId}/impact`,
      transformResponse: (response: unknown) => normalizeKeys<OrgImpact>(response),
    }),
    deleteOrganisation: builder.mutation<
      { orgId: string; name: string; status: string; deleted: boolean; message: string },
      { orgId: string; confirmName: string }
    >({
      query: ({ orgId, confirmName }) => ({
        url: `/portal/super/organisations/${orgId}`,
        method: 'DELETE',
        body: { confirm_name: confirmName },
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<{
          orgId: string;
          name: string;
          status: string;
          deleted: boolean;
          message: string;
        }>(response),
    }),
  }),
});

export const {
  useGetPricingDefaultsQuery,
  useUpdatePricingDefaultsMutation,
  useListOrgPricingQuery,
  useGetOrgPricingQuery,
  useUpdateOrgPricingMutation,
  useResetOrgPricingMutation,
  useGetStaffAnalyticsSummaryQuery,
  useGetStaffAnalyticsTimeseriesQuery,
  useGetStaffAnalyticsBreakdownQuery,
  useSearchStaffOrganisationsQuery,
  useSearchSuperOrganisationsQuery,
  useLazySearchSuperOrganisationsQuery,
  useGetOrganisationImpactQuery,
  useLazyGetOrganisationImpactQuery,
  useDeleteOrganisationMutation,
} = staffApi;
